import { useEffect, useRef } from "preact/hooks";
import type { PopoverState } from "./popover";

// A disabled button is shown but cannot take focus, so the arrows skip it; a link stays reachable.
const ITEMS = "[role=menuitem]:not(:disabled), [role=menuitemradio]:not(:disabled)";

// The keys of a menu in a popover: focus lands on its first item when it opens, the arrows walk the items and wrap, and
// Tab closes it with focus back on the trigger. `active` is false while the popover shows something that is not the menu.
export function useMenuKeys(popover: PopoverState<HTMLButtonElement>, active = true) {
	const wasOpen = useRef(false);
	const { panel, trigger, open, close } = popover;

	useEffect(() => {
		if (open && !wasOpen.current) panel.current?.querySelector<HTMLElement>(ITEMS)?.focus();
		wasOpen.current = open;
	}, [open, panel]);

	useEffect(() => {
		if (!open || !active) return undefined;
		const onKey = (event: KeyboardEvent) => {
			if (!panel.current?.contains(document.activeElement)) return;
			if (event.key === "Tab") {
				event.preventDefault();
				close();
				trigger.current?.focus();
				return;
			}
			if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
			event.preventDefault();
			const items = [...panel.current.querySelectorAll<HTMLElement>(ITEMS)];
			const at = items.indexOf(document.activeElement as HTMLElement);
			items[(at + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, active, panel, trigger, close]);
}
